import {useState, useEffect, useRef} from 'react';
import {useParams} from 'react-router-dom';
import {useError} from "@/contexts/ErrorProvider";
import {
    apiGetShredsList,
    apiGenerateShreds,
    apiGenerateSingleShred,
    ShredResponse,
    ServiceStatusResponse,
    SingleShredGenerationResponse,
} from "@/services/MicroServiceService";

type ShredsList = ShredResponse[];

type ShredGenerationResponse = {
    status: number;
    state: string;
    body: ShredsList;
};

export const useShredsData = () => {

    const {setIsError} = useError();
    const [initialLoading, setInitialLoading] = useState<boolean>(true); // NEW: separate initial loading state
    const [loading, setLoading] = useState<boolean>(false);
    const [serviceStatus, setServiceStatus] = useState<"IDLE" | "GENERATING_SHREDS" | "FAILED">("IDLE");
    const [shredsArr, setShreds] = useState<ShredsList>([]);
    const [generateShredModel, setGenerateShredModel] = useState(true);
    const [serviceInfo, setServiceInfo] = useState<{ name: string; status: string } | null>(null);
    const [generatingShredId, setGeneratingShredId] = useState<string | null>(null);
    const [error, setError] = useState("");
    const [isWaitingForAPI, setIsWaitingForAPI] = useState<boolean>(false);
    const [startTour, setStartTour] = useState(false);

    const {serviceId} = useParams();
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    const loadShreds = (isInitial = false) => {
        setIsError("");
        if (isInitial) {
            setInitialLoading(true); // Only set initial loading on first load
        }

        if (!serviceId) {
            setIsError("Service ID is missing");
            setInitialLoading(false);
            return;
        }

        apiGetShredsList<ServiceStatusResponse>(serviceId)
            .then((res) => {
                if (res.data) {
                    setServiceStatus(res.data.status);
                    setShreds(res.data.shreds);
                    setServiceInfo({
                        name: res.data.microServiceName,
                        status: res.data.status
                    });

                    res.data.shreds.find(shred =>
                        !shred.model &&
                        shred.summary &&
                        ['SPECS_GENERATED', 'DONE'].includes(shred.status)
                    );

                    const isAnyGeneratingFromServer = res.data.shreds.some(shred => shred.status === 'GENERATING_SPECS');
                    if (!isAnyGeneratingFromServer) {
                        setIsWaitingForAPI(false);
                    }

                    if (intervalRef.current) {
                        clearInterval(intervalRef.current);
                        intervalRef.current = null;
                    }

                    if (res.data.status === "GENERATING_SHREDS") {
                        setLoading(true);
                        const newIntervalId = setInterval(() => {
                            apiGetShredsList<ServiceStatusResponse>(serviceId)
                                .then((intervalRes) => {
                                    if (intervalRes.data) {
                                        setServiceStatus(intervalRes.data.status);
                                        setShreds(intervalRes.data.shreds);
                                        setServiceInfo({
                                            name: intervalRes.data.microServiceName,
                                            status: intervalRes.data.status
                                        });

                                        //Check if any feature shred just completed spec generation
                                        // const justCompletedFeature = intervalRes.data.shreds.find(shred =>
                                        //     !shred.model &&
                                        //     shred.summary &&
                                        //     ['SPECS_GENERATED', 'DONE'].includes(shred.status)
                                        // );

                                        const stillGenerating = intervalRes.data.shreds.some(shred => shred.status === 'GENERATING_SPECS');
                                        if (!stillGenerating) {
                                            setIsWaitingForAPI(false);
                                        }

                                        if (intervalRes.data.status !== "GENERATING_SHREDS") {
                                            if (intervalRef.current) {
                                                clearInterval(intervalRef.current);
                                                intervalRef.current = null;
                                            }
                                            setLoading(false);
                                            setGeneratingShredId(null);
                                            setTimeout(() => setStartTour(true), 2000);
                                        }
                                    }
                                })
                                .catch((errors: any) => {
                                    setIsError(errors?.response?.data?.message || errors.toString());
                                    if (intervalRef.current) {
                                        clearInterval(intervalRef.current);
                                        intervalRef.current = null;
                                    }
                                    setLoading(false);
                                    setGeneratingShredId(null);
                                    setIsWaitingForAPI(false);
                                });
                        }, 5000);
                        intervalRef.current = newIntervalId;
                    } else {
                        setLoading(false);
                        setGeneratingShredId(null);
                        setTimeout(() => setStartTour(true), 2000);
                    }

                    if (res.data.status === "FAILED") {
                        setIsError("An error occurred while generating shreds");
                        setLoading(false);
                        setGeneratingShredId(null);
                        setIsWaitingForAPI(false);
                    }
                }

                // IMPORTANT: Always set initial loading to false after first API call
                if (isInitial) {
                    setInitialLoading(false);
                }
            })
            .catch((errors: any) => {
                setIsError(errors?.response?.data?.message || errors.toString());
                setLoading(false);
                setGeneratingShredId(null);
                setIsWaitingForAPI(false);

                // IMPORTANT: Set initial loading to false on error too
                if (isInitial) {
                    setInitialLoading(false);
                }
            });
    };

    const onGenerateShred = async () => {
        setError("");
        setLoading(true);

        apiGenerateShreds<ShredGenerationResponse>(serviceId ?? "", generateShredModel)
            .then(() => {
                loadShreds(); // Don't pass isInitial=true here
            })
            .catch((errors) => {
                setError(errors?.response?.data?.body?.message || errors?.response?.data?.message || errors.toString());
                setLoading(false);
            });
    };

    const onGenerateSingleShred = async (shredId: string) => {
        const dataModelShred = shredsArr.find(shred => shred.model);
        const shred = shredsArr.find(s => s.shredId === shredId);

        if (!shred?.model && dataModelShred &&
            !['SPECS_GENERATED', 'PROMPT_VALIDATED', 'DONE'].includes(dataModelShred.status)) {
            setError("Please generate specifications for the Data Model first before generating feature shred specs.");
            return;
        }

        const isAnyGenerating = shredsArr.some(s => s.status === 'GENERATING_SPECS') || isWaitingForAPI;
        if (isAnyGenerating) {
            setError("Another shred is currently generating specifications. Please wait for it to complete.");
            return;
        }

        setError("");
        setGeneratingShredId(shredId);
        setIsWaitingForAPI(true);

        setShreds(prevShreds =>
            prevShreds.map(s =>
                s.shredId === shredId
                    ? {...s, status: 'GENERATING_SPECS' as const}
                    : s
            )
        );

        try {
            await apiGenerateSingleShred<SingleShredGenerationResponse>(serviceId ?? "", shredId);

            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }

            const pollInterval = setInterval(() => {
                loadShreds(); // Don't pass isInitial=true here
            }, 5000);
            intervalRef.current = pollInterval;

        } catch (errors: any) {
            setError(errors?.response?.data?.body?.message || errors?.response?.data?.message || errors.toString());
            setGeneratingShredId(null);
            setIsWaitingForAPI(false);

            setShreds(prevShreds =>
                prevShreds.map(s =>
                    s.shredId === shredId
                        ? {...s, status: 'CREATED' as const }
                        : s
                )
            );
        }
    };

    useEffect(() => {
        loadShreds(true); // Pass isInitial=true only on mount
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, []);

    return {
        initialLoading, // NEW: return initial loading state
        loading,
        serviceStatus,
        shredsArr,
        generateShredModel,
        setGenerateShredModel,
        serviceInfo,
        generatingShredId,
        error,
        setError,
        isWaitingForAPI,
        startTour,
        loadShreds,
        onGenerateShred,
        onGenerateSingleShred,
        setShreds
    };
};